// @flow
import React, { Component, Fragment } from 'react'
import _ from 'lodash'
import styled, { type StyledComponent } from 'styled-components'
import { Table, HeaderRow, HeaderCell, BodyRow, Cell, Link } from './MainTable'
import { grayColor } from './controls/colors'
import AdImagePreview from './controls/AdImagePreview'
import Checkbox from './controls/Checkbox'
import MassOperationsFooter from './controls/MassOperationsFooter'
import DescriptionUrlFormContainer from './AdvertisementTemplatesTable/DescriptionUrlFormContainer'
import Modal from './FieldSet/Modal'
import FilterDatepicker from './controls/FilterDatepicker'
import Switcher from './controls/Switcher'

export const editPath = (id: number) => `/advertisement_templates/${id}/edit`
export const advertisementEditPath = (id: number) => `/advertisements/${id}/edit`
export const audienceEditPath = (id: number) => `/audiences/${id}/edit`
const archivePath = '/api/v1/advertisement_templates_statuses/archive'

const StyledCell: StyledComponent<Object, any, any> = styled(Cell)`
  ${({ isTall }: Object) => (isTall ? `border-right: solid 1px ${grayColor}` : '')};
`
const CenterWrapper: StyledComponent<Object, any, any> = styled.div`
  display: flex;
  align-items: center;
`
const StyledCheckbox: StyledComponent<Object, any, any> = styled(Checkbox)`
  margin-right: 10px;
`
const SwitcherWrapper: StyledComponent<Object, any, any> = styled.div`
  padding-top: 10px;
`

const t = {
  advertisements: 'Объявления',
  average_cpc: 'eCPC',
  average_cpm: 'eCPM',
  audiences: 'Аудитории',
  templates: 'Шаблоны',
  clicks: 'Клики',
  ctr: 'CTR',
  impressions: 'Показы',
  modal_header: 'Изменение текстов/URL объявлений',
  name: 'Название',
  no_advertisements: 'Привязанные объявления отсутствуют',
  by_templates: 'По шаблонам',
  by_audiences: 'По аудиториям'
}

type Stats = {|
  impressions: string,
  clicks: string,
  ctr: string,
  average_cpc: string,
  average_cpm: string
|}

type AdvertisementItem = {|
  id: number,
  entity_id: number,
  entity_name: string,
  stats: Stats
|}

export type TemplateItem = {|
  id: number,
  name: string,
  picture: string,
  advertisements: Array<AdvertisementItem>
|}

export type AudienceItem = {|
  id: number,
  name: string,
  advertisements: Array<AdvertisementItem>
|}

type Props = {|
  isLoading: boolean,
  checkedTemplateIds: Array<number>,
  archivedTemplateIds: Array<number>,
  isModalOpen: boolean,
  filterFormOrigin: string,
  dateSeparator: string,
  initialDate: string,
  templateList: Array<TemplateItem>,
  audienceList: Array<AudienceItem>,
  tableGroupType: string,
  groupByTemplates: boolean,
  checkedItemIds: Array<number>,
  onChange: Function,
  onInit: Function,
  onDatepickerChange: Function,
  onTableGroupTypeChange: Function
|}

export default class AdvertisementTemplatesTable extends Component<Props> {
  constructor(props: Props) {
    super(props)
    props.onInit()
  }

  render() {
    return (
      <div>
        <Modal
          maxWidth={500}
          header={t.modal_header}
          isOpen={this.props.isModalOpen}
          hideBtn
          onClose={() => this.props.onChange({ isModalOpen: false })}
        >
          <DescriptionUrlFormContainer
            onCloseModal={() => this.props.onChange({ isModalOpen: false })}
          />
        </Modal>
        <FilterDatepicker
          initialDate={this.props.initialDate}
          separator={this.props.dateSeparator}
          origin={this.props.filterFormOrigin}
          disabled={this.props.isLoading}
          onChange={(value) => this.props.onDatepickerChange(value)}
        />
        <SwitcherWrapper>
          <Switcher
            options={[
              { id: 'templates', name: t.by_templates },
              { id: 'audiences', name: t.by_audiences }
            ]}
            value={this.props.tableGroupType}
            onChange={(tableGroupType: string) => this.props.onTableGroupTypeChange(tableGroupType)}
          />
        </SwitcherWrapper>
        <Table className="t-AdvertisementTemplateTable">
          <thead>
            <HeaderRow>
              <HeaderCell alignLeft>{t.name}</HeaderCell>
              {this.props.groupByTemplates ? (
                <HeaderCell alignLeft>{t.audiences}</HeaderCell>
              ) : (
                <HeaderCell alignLeft>{t.templates}</HeaderCell>
              )}
              <HeaderCell alignLeft>{t.advertisements}</HeaderCell>
              <HeaderCell alignLeft>{t.impressions}</HeaderCell>
              <HeaderCell alignLeft>{t.clicks}</HeaderCell>
              <HeaderCell alignLeft>{t.ctr}</HeaderCell>
              <HeaderCell alignLeft>{t.average_cpc}</HeaderCell>
              <HeaderCell alignLeft>{t.average_cpm}</HeaderCell>
            </HeaderRow>
          </thead>
          <tbody>
            {_.map(_.union(this.props.audienceList, this.props.templateList), (item) => (
              <Fragment key={item.id}>
                <BodyRow>
                  <StyledCell
                    isTall={item.advertisements.length > 1}
                    rowSpan={_.max([item.advertisements.length, 1])}
                  >
                    <CenterWrapper>
                      <StyledCheckbox
                        className="t-AdvertisementTemplateTableCheckbox"
                        onChange={(checked) => {
                          if (this.props.groupByTemplates) {
                            this.onCheckboxChange(checked, [item.id])
                          } else {
                            this.onCheckboxChange(checked, _.map(item.advertisements, 'entity_id'))
                          }
                        }}
                        checked={this.isChecked(item.id)}
                      />
                      {this.props.groupByTemplates && item.picture && (
                        <AdImagePreview pictureUrl={item.picture} />
                      )}
                      <Link
                        className="t-ItemNameLink"
                        href={
                          this.props.groupByTemplates
                            ? editPath(item.id)
                            : audienceEditPath(item.id)
                        }
                      >
                        {item.name}
                      </Link>
                    </CenterWrapper>
                  </StyledCell>
                  {this.renderAdvertisementCells(item.advertisements[0])}
                </BodyRow>
                {_.map(_.slice(item.advertisements, 1), (advertisement) => (
                  <BodyRow key={advertisement.id}>
                    {this.renderAdvertisementCells(advertisement)}
                  </BodyRow>
                ))}
              </Fragment>
            ))}
          </tbody>
        </Table>
        {!_.isEmpty(this.props.checkedTemplateIds) && this.renderFooter()}
      </div>
    )
  }

  renderAdvertisementCells(advertisement: ?AdvertisementItem) {
    if (!advertisement)
      return (
        <Fragment>
          <Cell colSpan={2}>{t.no_advertisements}</Cell>
        </Fragment>
      )

    return (
      <Fragment>
        <Cell>
          <Link
            href={
              this.props.groupByTemplates
                ? audienceEditPath(advertisement.entity_id)
                : editPath(advertisement.entity_id)
            }
          >
            [#{advertisement.entity_id}] {advertisement.entity_name}
          </Link>
        </Cell>
        <Cell>
          <Link href={advertisementEditPath(advertisement.id)}>#{advertisement.id}</Link>
        </Cell>
        <Cell>{advertisement.stats.impressions}</Cell>
        <Cell>{advertisement.stats.clicks}</Cell>
        <Cell>{advertisement.stats.ctr}</Cell>
        <Cell>{advertisement.stats.average_cpc}</Cell>
        <Cell>{advertisement.stats.average_cpm}</Cell>
      </Fragment>
    )
  }

  renderFooter() {
    return (
      <MassOperationsFooter
        isLoading={this.props.isLoading}
        archiveUrl={archivePath}
        ids={this.props.checkedTemplateIds}
        onBeforeRequest={() => this.props.onChange({ isLoading: true })}
        onAfterRequest={() => this.props.onChange({ isLoading: false })}
        onArchiveSuccess={() => this.onArchiveSuccess()}
        onTextUrlEdit={() => this.props.onChange({ isModalOpen: true })}
      />
    )
  }

  isChecked(id: number) {
    return _.includes(this.props.checkedItemIds, id)
  }

  onCheckboxChange(checked: boolean, templateIds: Array<number>) {
    const { checkedTemplateIds, onChange } = this.props

    if (checked) {
      onChange({ checkedTemplateIds: [...checkedTemplateIds, ...templateIds] })
    } else {
      onChange({ checkedTemplateIds: _.without(checkedTemplateIds, ...templateIds) })
    }
  }

  onArchiveSuccess() {
    const { checkedTemplateIds, archivedTemplateIds, onChange } = this.props
    onChange({
      archivedTemplateIds: [...archivedTemplateIds, ...checkedTemplateIds],
      checkedTemplateIds: []
    })
  }
}
